import { isEqual, sortBy } from 'lodash-es';
import { z } from 'zod';
import type { ProgrammingPlanFieldSetting } from '../SpecificData/FieldConfigInput';
import type { FieldInheritance } from '../SpecificData/FieldInheritance';
import type { ProgrammingPlanNationalStatus } from './ProgrammingPlanLocalStatus';
import {
  managedKey,
  type ProgrammingPlanSettingKey,
  type ProgrammingPlanSettings
} from './ProgrammingPlanSettings';
import { isCampaignLaunched } from './ProgrammingPlans';

export const ProgrammingPlanEditionPhase = z.enum([
  'Draft',
  'Submitted',
  'Launched'
]);
export type ProgrammingPlanEditionPhase = z.infer<
  typeof ProgrammingPlanEditionPhase
>;

export const programmingPlanEditionPhase = (plan: {
  launchedAt?: Date | null;
  nationalStatus: Pick<ProgrammingPlanNationalStatus, 'status'>;
}): ProgrammingPlanEditionPhase =>
  isCampaignLaunched(plan)
    ? 'Launched'
    : ['InProgress', 'SubmittedToAdmin'].includes(plan.nationalStatus.status)
      ? 'Draft'
      : 'Submitted';

const submittedLockedSettingKeys: ProgrammingPlanSettingKey[] = [
  'stages',
  'matrices',
  'substanceKinds',
  'context',
  'delegated'
];

export const lockedSettingKeys: Record<
  ProgrammingPlanEditionPhase,
  ProgrammingPlanSettingKey[]
> = {
  Draft: [],
  Submitted: submittedLockedSettingKeys,
  Launched: [
    ...submittedLockedSettingKeys,
    'samples',
    'monoSubstances',
    'multiSubstances'
  ]
};

export const isSettingLocked = (
  phase: ProgrammingPlanEditionPhase,
  settingKey: ProgrammingPlanSettingKey
): boolean => lockedSettingKeys[phase].includes(settingKey);

export const areFieldsLocked = (phase: ProgrammingPlanEditionPhase): boolean =>
  phase === 'Launched';

export const lockedSettingReason = (
  phase: ProgrammingPlanEditionPhase
): string | undefined => {
  switch (phase) {
    case 'Draft':
      return undefined;
    case 'Submitted':
      return 'Le plan a été soumis aux régions. Certains paramètres ne sont plus modifiables.';
    case 'Launched':
      return 'La campagne est lancée pour ce plan. Seuls certains paramètres sont modifiables.';
  }
};

type LockableFieldSetting = ProgrammingPlanFieldSetting & {
  inheritance?: FieldInheritance;
};

const comparableFields = (fields: LockableFieldSetting[]) =>
  sortBy(
    fields.map(({ fieldId, required, optionIds, inheritance }) => ({
      fieldId,
      required,
      optionIds: [...optionIds].sort(),
      inheritance
    })),
    'fieldId'
  );

type LockableSettings = ProgrammingPlanSettings & {
  fields: LockableFieldSetting[];
};

export const lockedSettingsChanged = (
  phase: ProgrammingPlanEditionPhase,
  stored: LockableSettings,
  body: LockableSettings
): boolean =>
  lockedSettingKeys[phase].some(
    (settingKey) =>
      stored[managedKey(settingKey)] !== body[managedKey(settingKey)] ||
      (body[managedKey(settingKey)] &&
        !isEqual(stored[settingKey], body[settingKey]))
  ) ||
  (areFieldsLocked(phase) &&
    !isEqual(comparableFields(stored.fields), comparableFields(body.fields)));
